import React from 'react'
import { createPortal } from 'react-dom'
import { Button, Heading, Text } from '../ui'
import {
  CLOSING_SCRIPT,
  MAIN_REASON_IF_DIFFICULTY,
  MAIN_REASON_IF_REFUSES,
  MAIN_REASON_OPENING,
  RISK_OPENING,
  RISK_QUESTIONS,
  RISK_ROLE_NOTE,
  TriageAttendantHint,
  TriageNote,
  TriageQuote,
  openingScript,
} from './triagemAttendantScripts'
import { AgendaCancelModal } from './AgendaCancelModal'
import { AppointmentAttachmentsList } from './AppointmentAttachmentsList'
import type { SupportCenterAppointmentCancelChannel } from '../../types/supportCenterAppointments'
import { appointmentChannelLabel } from '../../types/supportCenterAppointments'
import { useUserLogs } from '../../hooks/useUserLogs'
import { useAuthContext } from '../../contexts/AuthContext'
import { supportCenterTriageService } from '../../services/supportCenterTriage'
import {
  canPerformTriage,
  triagePastMessage,
} from '../../services/supportCenterAttendance'
import { supportCenterCancelReasonsService } from '../../services/supportCenterCancelReasons'
import type { SupportCenterProfileOption } from '../../types/supportCenterSpecialties'
import type { SupportCenterCancelReason } from '../../types/supportCenterCancelReasons'
import { DEFAULT_SCHEDULING_LIMITS } from '../../types/supportCenterParameters'
import type {
  SupportCenterDemandType,
  SupportCenterReferralOrigin,
  SupportCenterTriageFormData,
  SupportCenterTriageListItem,
} from '../../types/supportCenterTriage'
import {
  EMPTY_TRIAGE_FORM_DATA,
  SUPPORT_CENTER_FORM_KIND_LABELS,
  TRIAGE_DEMAND_NATURE_OPTIONS,
  TRIAGE_RISK_OPTIONS,
  TRIAGE_SUFFERING_OPTIONS,
  formatBirthYearAndAge,
  EMPTY_TRIAGE_JURIDICAL,
  EMPTY_TRIAGE_OBJECTIVE,
  isReducedTriageForm,
  reducedTriageRequiredMessage,
  reducedTriageText,
  triageMainReasonLabel,
  triageRowToFormData,
  withReducedTriageText,
} from '../../types/supportCenterTriage'
import { appointmentFormKind, ASSISTANCE_FORM_KIND_MISSING } from '../../types/supportCenterSpecialties'
import { isTriageClosedWithoutAttendance } from './triagemHelpers'
import {
  DASHBOARD_INPUT_CLASS,
  formatCpf,
  formatPhone,
  getErrorMessage,
  onlyDigits,
  patientKinshipLabel,
  todayIso,
} from './dashboardHelpers'
import {
  AttendanceDemandSelector,
  getSelectedDemandTypesRequiringNotes,
} from './AttendanceDemandSelector'
import { primaryClientEmail } from './triagemHelpers'

const REJECT_BUTTON_CLASS =
  '!bg-red-600 !text-white hover:!bg-red-700 dark:!bg-red-600 dark:hover:!bg-red-700 dark:!text-white ring-red-600'

function StaticField({ label, value }: { label: string; value: string }) {
  const text = value.trim() || '—'
  return (
    <div className="min-w-0">
      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="mt-0.5 text-sm text-gray-900 dark:text-white break-words">{text}</dd>
    </div>
  )
}

type TriagemCaseModalProps = {
  triageCase: SupportCenterTriageListItem
  canUpdate: boolean
  profiles: SupportCenterProfileOption[]
  referralOrigins: SupportCenterReferralOrigin[]
  demandTypes: SupportCenterDemandType[]
  pastToleranceMinutes?: number
  onClose: () => void
  onSaved: () => void | Promise<void>
}

function buildInitialFormData(
  item: SupportCenterTriageListItem,
  internFallback: string
): SupportCenterTriageFormData {
  const form = triageRowToFormData(item)
  return {
    ...EMPTY_TRIAGE_FORM_DATA,
    ...form,
    triage_date: form.triage_date || todayIso(),
    intern_id: form.intern_id || internFallback,
    emergency_contact_phone: formatPhone(form.emergency_contact_phone),
    demand_selections: form.demand_selections ?? [],
    psychological: {
      ...EMPTY_TRIAGE_FORM_DATA.psychological,
      ...form.psychological,
    },
    nutritional: {
      ...EMPTY_TRIAGE_OBJECTIVE,
      ...form.nutritional,
    },
    fitness: {
      ...EMPTY_TRIAGE_OBJECTIVE,
      ...form.fitness,
    },
    juridical: {
      ...EMPTY_TRIAGE_JURIDICAL,
      ...form.juridical,
    },
    social: {
      ...EMPTY_TRIAGE_JURIDICAL,
      ...form.social,
    },
  }
}

export const TriagemCaseModal: React.FC<TriagemCaseModalProps> = ({
  triageCase,
  canUpdate,
  profiles,
  referralOrigins,
  demandTypes,
  pastToleranceMinutes = DEFAULT_SCHEDULING_LIMITS.pastToleranceMinutes,
  onClose,
  onSaved,
}) => {
  const { user, profile } = useAuthContext()
  const { logUpdate } = useUserLogs()

  const internFallback = profile?.id || user?.id || ''
  const holderCpf = triageCase.appointment?.holder_cpf ?? ''
  const patientCpf =
    triageCase.appointment?.patient_cpf ||
    triageCase.appointment?.patient_people?.tax_id_number ||
    ''
  const patientName = triageCase.appointment?.patient_people?.name ?? ''
  const patientBirthDate = triageCase.appointment?.patient_people?.captured_birth_date ?? null
  const kinshipLabel =
    patientKinshipLabel({
      holder_cpf: holderCpf,
      patient_cpf: patientCpf,
      relationship_to_holder: triageCase.appointment?.relationship_to_holder,
    }) ?? ''

  const [formData, setFormData] = React.useState<SupportCenterTriageFormData>(() =>
    buildInitialFormData(triageCase, internFallback)
  )
  const [rejectConfirmOpen, setRejectConfirmOpen] = React.useState(false)
  const [rejectModalOpen, setRejectModalOpen] = React.useState(false)
  const [rejectionReasonId, setRejectionReasonId] = React.useState('')
  const [cancellationChannel, setCancellationChannel] = React.useState<
    '' | SupportCenterAppointmentCancelChannel
  >('')
  const [rejectionReasons, setRejectionReasons] = React.useState<SupportCenterCancelReason[]>([])
  const [loadingRejectionReasons, setLoadingRejectionReasons] = React.useState(false)
  const [rejectError, setRejectError] = React.useState<string | null>(null)
  const [modalError, setModalError] = React.useState<string | null>(null)
  const [submitting, setSubmitting] = React.useState(false)

  React.useEffect(() => {
    setFormData(buildInitialFormData(triageCase, profile?.id || user?.id || ''))
    setRejectConfirmOpen(false)
    setRejectModalOpen(false)
    setRejectionReasonId('')
    setCancellationChannel('')
    setRejectError(null)
    setModalError(null)
    // Reinicia ao trocar o caso ou quando os dados do mesmo caso forem atualizados
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triageCase.id, triageCase.updated_at])

  React.useEffect(() => {
    if (!rejectModalOpen || triageCase.status !== 'pendente') return
    let cancelled = false
    setLoadingRejectionReasons(true)
    setRejectError(null)
    void supportCenterCancelReasonsService
      .listByKind('triagem')
      .then((list) => {
        if (cancelled) return
        setRejectionReasons(list)
        if (list.length === 0) {
          setRejectError(
            'Nenhum motivo cadastrado. Cadastre em Parâmetros > Motivos de cancelamento > Triagem.'
          )
        }
      })
      .catch((error) => {
        if (cancelled) return
        setRejectionReasons([])
        setRejectError(getErrorMessage(error))
      })
      .finally(() => {
        if (!cancelled) setLoadingRejectionReasons(false)
      })
    return () => {
      cancelled = true
    }
  }, [rejectModalOpen, triageCase.status])

  const isEditable = canPerformTriage(triageCase, true, pastToleranceMinutes)
  const formKind = appointmentFormKind(triageCase.appointment)
  const specificFormKey =
    triageCase.appointment?.assistance?.specific_form?.form_key?.trim() ||
    triageCase.appointment?.assistance?.specific_form_key?.trim() ||
    null
  const formCatalogName = triageCase.appointment?.assistance?.specific_form?.name?.trim() || null
  const formLabel =
    formCatalogName ||
    (formKind ? SUPPORT_CENTER_FORM_KIND_LABELS[formKind] : null) ||
    formKind
  const hasCatalogForm = Boolean(specificFormKey)
  const hasOperationalForm = Boolean(formKind)
  const appointmentAssistanceId = triageCase.appointment?.assistance_id?.trim() || null
  const operationalFormMessage = hasCatalogForm
    ? `Este formulário${formLabel ? ` (${formLabel})` : ''} ainda não tem fluxo de triagem implementado.`
    : ASSISTANCE_FORM_KIND_MISSING
  const isPsychologicalForm = formKind === 'psicologico'
  const isReducedForm = isReducedTriageForm(formKind)
  const mainReasonLabel = triageMainReasonLabel(formKind)
  const selectedInternName =
    profiles.find((p) => p.id === formData.intern_id)?.name ||
    triageCase.intern?.name ||
    profile?.name ||
    ''
  const patientBirthLabel = formatBirthYearAndAge(
    patientBirthDate,
    formData.triage_date || todayIso()
  )
  const visibleDemandTypes = React.useMemo(() => {
    if (!appointmentAssistanceId) return []
    return demandTypes.filter((item) =>
      item.assistances.some((assistance) => assistance.id === appointmentAssistanceId)
    )
  }, [appointmentAssistanceId, demandTypes])

  const closeModal = () => {
    if (submitting) return
    onClose()
  }

  const selectedReferralOrigin = referralOrigins.find(
    (item) => item.id === formData.referral_origin_id
  )
  const demandTypesWithNotes = getSelectedDemandTypesRequiringNotes(
    visibleDemandTypes,
    formData.demand_selections
  )
  const patientLabel =
    patientName.trim() ||
    triageCase.appointment?.patient_people?.name ||
    onlyDigits(patientCpf) ||
    'paciente'

  const validateFormBasics = (): string | null => {
    if (!onlyDigits(patientCpf)) return 'Valide o CPF do paciente.'
    if (!formData.triage_date || !formData.intern_id) {
      return 'Informe a data da triagem e o responsável.'
    }
    if (!formKind) {
      return operationalFormMessage
    }
    return null
  }

  const handleComplete = async () => {
    if (!user?.id || !isEditable) return
    const basicError = validateFormBasics()
    if (basicError) {
      setModalError(basicError)
      return
    }
    if (!formData.referral_origin_id) {
      setModalError('Selecione a origem do encaminhamento.')
      return
    }
    if (selectedReferralOrigin?.requires_notes && !formData.referral_origin_other.trim()) {
      setModalError('Descreva a origem do encaminhamento.')
      return
    }
    const missingDemandNotes = demandTypesWithNotes.find(
      (item) =>
        !(formData.demand_selections.find((selection) => selection.demand_type_id === item.id)?.notes ?? '')
          .trim()
    )
    if (missingDemandNotes) {
      setModalError(`Descreva o complemento da demanda "${missingDemandNotes.name}".`)
      return
    }
    if (!formKind) return
    const kind = formKind
    if (isReducedTriageForm(kind)) {
      if (!reducedTriageText(formData, kind).trim()) {
        setModalError(reducedTriageRequiredMessage(kind))
        return
      }
      if (formData.demand_selections.length === 0) {
        setModalError('Selecione ao menos um tipo de demanda.')
        return
      }
    }

    setSubmitting(true)
    setModalError(null)
    try {
      await supportCenterTriageService.completeTriage({
        id: triageCase.id,
        form_kind: kind,
        form: formData,
        actor_id: user.id,
        appointment_id: triageCase.appointment?.id ?? null,
      })
      void logUpdate(
        'support_center_triage',
        triageCase.id,
        `Concluiu triagem: ${patientLabel} (${onlyDigits(patientCpf)})`
      )
      onClose()
      await onSaved()
    } catch (error) {
      setModalError(getErrorMessage(error))
    } finally {
      setSubmitting(false)
    }
  }

  const openRejectConfirm = () => {
    if (!isEditable || submitting) return
    setModalError(null)
    setRejectConfirmOpen(true)
  }

  const handleConfirmRejectIntent = async () => {
    if (!user?.id || !isEditable) return
    const basicError = validateFormBasics()
    if (basicError) {
      setRejectConfirmOpen(false)
      setModalError(basicError)
      return
    }
    if (!formKind) return
    const kind = formKind

    setSubmitting(true)
    setModalError(null)
    try {
      await supportCenterTriageService.saveTriageForm({
        id: triageCase.id,
        form_kind: kind,
        form: formData,
        appointment_id: triageCase.appointment?.id ?? null,
      })
      void logUpdate(
        'support_center_triage',
        triageCase.id,
        `Salvou formulário da triagem antes da reprovação: ${patientLabel} (${onlyDigits(patientCpf)})`
      )
      setRejectConfirmOpen(false)
      setRejectionReasonId('')
      setCancellationChannel('')
      setRejectModalOpen(true)
    } catch (error) {
      setRejectConfirmOpen(false)
      setModalError(getErrorMessage(error))
    } finally {
      setSubmitting(false)
    }
  }

  const closeRejectModal = () => {
    if (submitting) return
    setRejectModalOpen(false)
    setRejectError(null)
    setRejectionReasonId('')
    setCancellationChannel('')
  }

  const handleReject = async () => {
    if (!user?.id || !isEditable) return
    const selectedReason = rejectionReasons.find((r) => r.id === rejectionReasonId)
    if (!selectedReason) {
      setRejectError(
        rejectionReasons.length === 0
          ? 'Nenhum motivo cadastrado. Cadastre em Parâmetros > Motivos de cancelamento > Triagem.'
          : 'Selecione o motivo da reprovação.'
      )
      return
    }
    if (
      cancellationChannel !== 'whatsapp' &&
      cancellationChannel !== 'telefone' &&
      cancellationChannel !== 'videochamada'
    ) {
      setRejectError('Selecione o canal do cancelamento.')
      return
    }

    setSubmitting(true)
    setRejectError(null)
    try {
      const rejection_reason = selectedReason.name.trim()
      const contextAppointmentId = triageCase.appointment?.id?.trim()
      if (!contextAppointmentId) {
        setRejectError('Não há agendamento de contexto para cancelar com esta reprovação.')
        return
      }
      await supportCenterTriageService.rejectTriage({
        id: triageCase.id,
        appointment_id: contextAppointmentId,
        cancel_reason_id: selectedReason.id,
        actor_id: user.id,
        cancellation_channel: cancellationChannel,
      })
      void logUpdate(
        'support_center_triage',
        triageCase.id,
        `Reprovou triagem: ${patientLabel} — ${rejection_reason}`
      )
      setRejectModalOpen(false)
      setCancellationChannel('')
      onClose()
      await onSaved()
    } catch (error) {
      setRejectError(getErrorMessage(error))
    } finally {
      setSubmitting(false)
    }
  }

  const renderRadioGroup = <T extends string>(
    name: string,
    value: T,
    options: Array<{ value: T; label: string }>,
    onChange: (next: T) => void
  ) => (
    <div className="flex flex-wrap gap-3">
      {options.map((option) => (
        <label
          key={option.value}
          className="inline-flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200"
        >
          <input
            type="radio"
            name={name}
            checked={value === option.value}
            disabled={!isEditable}
            onChange={() => onChange(option.value)}
            className="text-primary-600 focus:ring-primary-500"
          />
          {option.label}
        </label>
      ))}
    </div>
  )

  return createPortal(
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="triagem-case-modal-title"
        className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-4xl max-h-[92vh] flex flex-col"
      >
        <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div className="min-w-0">
            <Heading
              as="h2"
              size="2xl"
              id="triagem-case-modal-title"
              className="text-gray-900 dark:text-white"
            >
              {isEditable ? 'Realizar triagem' : 'Detalhe da triagem'}
            </Heading>
          </div>
          <Button variant="ghost" onClick={closeModal} disabled={submitting}>
            Fechar
          </Button>
        </div>

        <div className="px-6 py-5 overflow-y-auto space-y-6 flex-1 min-h-0">

        {triageCase.status === 'nao_realizado' ||
        (triageCase.status === 'pendente' &&
          triageCase.appointment?.scheduled_at &&
          !isEditable) ? (
          <div className="mb-4 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-800/40">
            <Text size="sm" className="text-slate-700 dark:text-slate-200">
              {triagePastMessage(pastToleranceMinutes)}
            </Text>
          </div>
        ) : null}

        <div className="space-y-8">
          <TriageAttendantHint title="Abertura da conversa (fale com o paciente)">
            {openingScript(
              selectedInternName,
              formKind,
              triageCase.appointment?.specialization?.name
            ).map((line) => (
              <TriageQuote key={line}>“{line}”</TriageQuote>
            ))}
          </TriageAttendantHint>

          <div className="space-y-6">
            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
              Dados gerais
            </Heading>

            <dl className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-3">
              <StaticField
                label="Nome do segurado"
                value={triageCase.appointment?.holder_people?.name ?? ''}
              />
              <StaticField label="CPF do segurado" value={formatCpf(holderCpf)} />
              <StaticField label="Nascimento / idade" value={patientBirthLabel} />
              <StaticField
                label="Nome do paciente"
                value={
                  patientName ||
                  (onlyDigits(patientCpf).length === 11 ? 'Não encontrado' : '')
                }
              />
              <StaticField label="CPF do paciente" value={formatCpf(patientCpf)} />
              <StaticField label="Parentesco" value={kinshipLabel} />
              <StaticField
                label="Telefone (agenda)"
                value={formatPhone(triageCase.appointment?.patient_phone)}
              />
              <StaticField
                label="E-mail (agenda)"
                value={primaryClientEmail(triageCase.appointment?.client_emails)}
              />
            </dl>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Data da triagem <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.triage_date}
                    disabled={!isEditable}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, triage_date: e.target.value }))
                    }
                    className={DASHBOARD_INPUT_CLASS}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Responsável pela triagem <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.intern_id}
                    disabled={!isEditable}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, intern_id: e.target.value }))
                    }
                    className={DASHBOARD_INPUT_CLASS}
                  >
                    <option value="">Selecione...</option>
                    {profiles.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                    {formData.intern_id &&
                      !profiles.some((p) => p.id === formData.intern_id) &&
                      triageCase.intern && (
                        <option value={triageCase.intern.id}>
                          {triageCase.intern.name ?? triageCase.intern.id}
                        </option>
                      )}
                  </select>
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Origem do encaminhamento <span className="text-red-500">*</span>
                </label>
                {renderRadioGroup(
                  'referral_origin_id',
                  formData.referral_origin_id,
                  referralOrigins.map((item) => ({ value: item.id, label: item.name })),
                  (next) =>
                    setFormData((prev) => ({
                      ...prev,
                      referral_origin_id: next,
                      referral_origin_other: '',
                    }))
                )}
                {selectedReferralOrigin?.requires_notes && (
                  <div className="mt-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Descreva a origem <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.referral_origin_other}
                      disabled={!isEditable}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          referral_origin_other: e.target.value,
                        }))
                      }
                      placeholder="Descreva a origem..."
                      className={DASHBOARD_INPUT_CLASS}
                    />
                  </div>
                )}
            </div>
          </div>

          <div className="space-y-6 border-t border-gray-200 dark:border-gray-700 pt-6">
            <div>
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                  Formulário específico
                </Heading>
                {hasOperationalForm && formLabel ? (
                  <Text size="sm" color="muted">
                    {formLabel}
                  </Text>
                ) : null}
              </div>
              {!hasOperationalForm && (
                <Text size="sm" color="muted" className="mt-1">
                  {operationalFormMessage}
                </Text>
              )}
            </div>

            {isPsychologicalForm && (
              <section className="space-y-3">
                <Heading as="h4" size="xl" className="text-gray-900 dark:text-white">
                  Complemento do paciente
                </Heading>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Contato de emergência com DDD
                    </label>
                    <input
                      type="text"
                      value={formData.emergency_contact_phone}
                      disabled={!isEditable}
                      placeholder="(00) 00000-0000"
                      inputMode="tel"
                      maxLength={15}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          emergency_contact_phone: formatPhone(e.target.value),
                        }))
                      }
                      className={DASHBOARD_INPUT_CLASS}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Parentesco com o contato de emergência
                    </label>
                    <input
                      type="text"
                      value={formData.emergency_contact_relation}
                      disabled={!isEditable}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          emergency_contact_relation: e.target.value,
                        }))
                      }
                      className={DASHBOARD_INPUT_CLASS}
                    />
                  </div>
                </div>
              </section>
            )}

            {(isPsychologicalForm || isReducedForm) && (
              <section className="space-y-3">
                <Heading as="h4" size="xl" className="text-gray-900 dark:text-white">
                  Relato do atendimento
                </Heading>
                {isPsychologicalForm && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Motivo principal da procura
                    </label>
                    <div className="mb-2 space-y-2">
                      <TriageAttendantHint title="Comece com pergunta aberta">
                        <TriageNote>
                          Dê espaço para o paciente falar com as próprias palavras.
                        </TriageNote>
                        <TriageQuote>“{MAIN_REASON_OPENING}”</TriageQuote>
                      </TriageAttendantHint>
                      <TriageAttendantHint title="Se tiver dificuldade, vergonha ou não quiser detalhes">
                        {MAIN_REASON_IF_DIFFICULTY.map((line) => (
                          <TriageQuote key={line}>“{line}”</TriageQuote>
                        ))}
                        <TriageNote>
                          Se perceber que outra opção do formulário pode estar relacionada ao
                          relato, pergunte de forma simples e natural.
                        </TriageNote>
                      </TriageAttendantHint>
                      <TriageAttendantHint title="Se o paciente não quiser falar">
                        <TriageNote>
                          Não insista e não pressione para obter uma resposta.
                        </TriageNote>
                        <TriageQuote>“{MAIN_REASON_IF_REFUSES}”</TriageQuote>
                        <TriageNote>
                          Registre somente as informações que foi possível identificar durante a
                          conversa.
                        </TriageNote>
                      </TriageAttendantHint>
                    </div>
                    <textarea
                      rows={4}
                      value={formData.main_reason}
                      disabled={!isEditable}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, main_reason: e.target.value }))
                      }
                      className={`${DASHBOARD_INPUT_CLASS} resize-none`}
                    />
                  </div>
                )}
                {isReducedForm && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {mainReasonLabel} <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      rows={4}
                      value={formKind ? reducedTriageText(formData, formKind) : ''}
                      disabled={!isEditable}
                      onChange={(e) =>
                        setFormData((prev) =>
                          formKind ? withReducedTriageText(prev, formKind, e.target.value) : prev
                        )
                      }
                      className={`${DASHBOARD_INPUT_CLASS} resize-none`}
                    />
                  </div>
                )}
                {isPsychologicalForm && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Disponibilidade
                    </label>
                    <textarea
                      rows={2}
                      value={formData.availability}
                      disabled={!isEditable}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, availability: e.target.value }))
                      }
                      className={`${DASHBOARD_INPUT_CLASS} resize-none`}
                    />
                  </div>
                )}
              </section>
            )}

            {formKind && (
              <section className="space-y-3">
                <Heading as="h4" size="xl" className="text-gray-900 dark:text-white">
                  Tipo de demanda percebida
                  {isReducedForm ? <span className="text-red-500"> *</span> : null}
                </Heading>
                <AttendanceDemandSelector
                  demandTypes={visibleDemandTypes}
                  selection={formData.demand_selections}
                  disabled={!isEditable}
                  onChange={(next) =>
                    setFormData((prev) => ({ ...prev, demand_selections: next }))
                  }
                />
              </section>
            )}

            {isPsychologicalForm && (
              <>
                <section className="space-y-3">
                  <Heading as="h4" size="xl" className="text-gray-900 dark:text-white">
                    Observações
                  </Heading>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Intensidade do sofrimento
                    </label>
                    {renderRadioGroup(
                      'suffering_intensity',
                      formData.psychological.suffering_intensity,
                      TRIAGE_SUFFERING_OPTIONS,
                      (next) =>
                        setFormData((prev) => ({
                          ...prev,
                          psychological: { ...prev.psychological, suffering_intensity: next },
                        }))
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      A demanda parece ser
                    </label>
                    {renderRadioGroup(
                      'demand_nature',
                      formData.psychological.demand_nature,
                      TRIAGE_DEMAND_NATURE_OPTIONS,
                      (next) =>
                        setFormData((prev) => ({
                          ...prev,
                          psychological: { ...prev.psychological, demand_nature: next },
                        }))
                    )}
                  </div>
                </section>

                <section className="space-y-4">
                  <Heading as="h4" size="xl" className="text-gray-900 dark:text-white">
                    Avaliação inicial de risco
                  </Heading>
                  <TriageAttendantHint title="Orientação ao atendente">
                    <TriageNote>{RISK_ROLE_NOTE}</TriageNote>
                    <TriageQuote>“{RISK_OPENING}”</TriageQuote>
                  </TriageAttendantHint>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Ideação suicida
                    </label>
                    <div className="mb-2">
                      <TriageQuote>“{RISK_QUESTIONS.suicidal_ideation}”</TriageQuote>
                    </div>
                    {renderRadioGroup(
                      'suicidal_ideation',
                      formData.psychological.suicidal_ideation,
                      TRIAGE_RISK_OPTIONS,
                      (next) =>
                        setFormData((prev) => ({
                          ...prev,
                          psychological: { ...prev.psychological, suicidal_ideation: next },
                        }))
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Autoagressão
                    </label>
                    <div className="mb-2">
                      <TriageQuote>“{RISK_QUESTIONS.self_harm}”</TriageQuote>
                    </div>
                    {renderRadioGroup(
                      'self_harm',
                      formData.psychological.self_harm,
                      TRIAGE_RISK_OPTIONS,
                      (next) =>
                        setFormData((prev) => ({
                          ...prev,
                          psychological: { ...prev.psychological, self_harm: next },
                        }))
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Risco de agressão a terceiros
                    </label>
                    <div className="mb-2">
                      <TriageQuote>“{RISK_QUESTIONS.aggression_risk}”</TriageQuote>
                    </div>
                    {renderRadioGroup(
                      'aggression_risk',
                      formData.psychological.aggression_risk,
                      TRIAGE_RISK_OPTIONS,
                      (next) =>
                        setFormData((prev) => ({
                          ...prev,
                          psychological: { ...prev.psychological, aggression_risk: next },
                        }))
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Suspeita de violência doméstica / abuso / negligência
                    </label>
                    <div className="mb-2 space-y-2">
                      <TriageQuote>“{RISK_QUESTIONS.domestic_violence}”</TriageQuote>
                      <TriageNote>Se o paciente não entender:</TriageNote>
                      <TriageQuote>“{RISK_QUESTIONS.domestic_violence_clarify}”</TriageQuote>
                    </div>
                    {renderRadioGroup(
                      'domestic_violence',
                      formData.psychological.domestic_violence,
                      TRIAGE_RISK_OPTIONS.filter((opt) => opt.value !== 'refused'),
                      (next) =>
                        setFormData((prev) => ({
                          ...prev,
                          psychological: { ...prev.psychological, domestic_violence: next },
                        }))
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Observações livres
                    </label>
                    <textarea
                      rows={3}
                      value={formData.psychological.observations}
                      disabled={!isEditable}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          psychological: {
                            ...prev.psychological,
                            observations: e.target.value,
                          },
                        }))
                      }
                      className={`${DASHBOARD_INPUT_CLASS} resize-none`}
                    />
                  </div>
                </section>
              </>
            )}
          </div>

          <TriageAttendantHint title="Fechamento da triagem">
            <TriageQuote>“{CLOSING_SCRIPT}”</TriageQuote>
          </TriageAttendantHint>

          {isTriageClosedWithoutAttendance(triageCase.status) && (
            <section className="space-y-2">
              <Heading as="h3" size="xl" className="text-gray-900 dark:text-white">
                {triageCase.status === 'cancelado' ? 'Cancelamento' : 'Reprovação'}
              </Heading>
              {triageCase.appointment?.cancellation_channel ? (
                <>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Canal do cancelamento
                  </label>
                  <div className={`${DASHBOARD_INPUT_CLASS} opacity-80`}>
                    {appointmentChannelLabel(triageCase.appointment.cancellation_channel)}
                  </div>
                </>
              ) : null}
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Motivo {triageCase.status === 'cancelado' ? 'do cancelamento' : 'da reprovação'}
              </label>
              <div className={`${DASHBOARD_INPUT_CLASS} opacity-80`}>
                {triageCase.cancel_reason?.name || '—'}
              </div>
              {triageCase.cancel_reason?.description ? (
                <>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 mt-2">
                    Descrição
                  </label>
                  <div className={`${DASHBOARD_INPUT_CLASS} opacity-80`}>
                    {triageCase.cancel_reason.description}
                  </div>
                </>
              ) : null}
            </section>
          )}

          {triageCase.appointment?.id ? (
            <AppointmentAttachmentsList
              appointmentId={triageCase.appointment.id}
              variant="section"
              title="Anexos"
              canUpload={canUpdate}
            />
          ) : null}

        </div>
        </div>

        {(modalError || (isEditable && canUpdate)) && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 shrink-0 space-y-3">
            {modalError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
                <Text size="sm">{modalError}</Text>
              </div>
            )}
            {isEditable && canUpdate && (
              <div className="flex flex-wrap justify-end gap-3">
                <Button
                  className={REJECT_BUTTON_CLASS}
                  onClick={openRejectConfirm}
                  disabled={submitting}
                >
                  Reprovar
                </Button>
                <Button variant="solid" onClick={handleComplete} disabled={submitting}>
                  {submitting ? 'Salvando...' : 'Concluir triagem'}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>

      {rejectConfirmOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6 space-y-4">
            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
              Confirmar reprovação
            </Heading>
            <Text size="sm" color="muted">
              Confirma a reprovação desta triagem? Os dados do formulário serão salvos antes de
              escolher o motivo.
            </Text>
            <div className="flex justify-end gap-3">
              <Button
                variant="ghost"
                onClick={() => setRejectConfirmOpen(false)}
                disabled={submitting}
              >
                Voltar
              </Button>
              <Button
                className={REJECT_BUTTON_CLASS}
                onClick={() => void handleConfirmRejectIntent()}
                disabled={submitting}
              >
                {submitting ? 'Salvando...' : 'Confirmar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {rejectModalOpen && (
        <AgendaCancelModal
          patientLabel={patientLabel}
          cancelReasonId={rejectionReasonId}
          cancelReasons={rejectionReasons}
          loadingCancelReasons={loadingRejectionReasons}
          cancelling={submitting}
          cancelError={rejectError}
          onReasonChange={setRejectionReasonId}
          cancellationChannel={cancellationChannel}
          onCancellationChannelChange={setCancellationChannel}
          onClose={closeRejectModal}
          onConfirm={() => void handleReject()}
          title="Reprovar triagem"
          description="Você está reprovando a triagem de {patient}. Somente o agendamento exibido neste formulário será cancelado."
          reasonLabel="Motivo da reprovação"
          confirmLabel="Confirmar reprovação"
          confirmingLabel="Reprovando..."
          pendingMessage="Confirmando a reprovação, aguarde..."
          overlayClassName="z-[60]"
        />
      )}
    </>,
    document.body
  )
}

export default TriagemCaseModal
